import { addSkuToBasket, BASKET_CLIENT_ID } from "./basketApi";
import {
  AddToBasketChildItemRequest,
  AddToBasketResponse,
  APIErrorCode,
  SubmissionContext,
  SubmissionResult,
  BasketSubmissionParams,
  BasketSubmissionCallbacks,
} from "../types";
import { getBasketItems } from "@/components/AddToBasket/AddToBasketLocal/utils/getBasketItems.ts";
import {
  updateBasketAnalyticsObject,
  updateExpressAnalytics,
  addToBasketSuccessPayload,
  addToBasketFailurePayload,
  ProductInfo,
  buildBasketUpdateDataFromBasket,
  buildTrackEventProductData,
} from "@/components/AddToBasket/AddToBasketLocal/utils/analyticsHelpers.ts";
import { fireMonetateEventIfReferredByPLP } from "@/components/AddToBasket/AddToBasketLocal/utils/monetateHelpers.ts";
import { dispatchCustomEvent } from "@/utils/customEvent/dispatchCustomEvent.ts";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ.ts";
import {
  AddToBasket,
  trackEvent,
} from "@JohnLewisPartnership/jl-analytics-event-stream";
import { addPageAction, noticeError } from "@/utils/newrelic/requests.ts";
import Cookies from "js-cookie";
import { sha3_256 } from "js-sha3";
import { dispatchAnalyticsEvent } from "@/utils";
import { ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED } from "@/constants/constants.ts";

const RESET_DELAY_MS = 300;
const MAX_RETRIES = 5;

/**
 * Default hooks for basket submission lifecycle.
 * These provide logging points for tracking submission events.
 * Override these to add custom event dispatching or monitoring.
 */
const defaultSubmissionHooks = {
  onStart: (_context: SubmissionContext) => {
    if (_context.isExpressCheckout) {
      dispatchMonetateQEvent("express_checkout_click");
    } else {
      dispatchMonetateQEvent("add_to_basket_click");
    }
  },
  onSubmitted: (_context: SubmissionContext) => {
    if (_context.isExpressCheckout) {
      updateExpressAnalytics();
    } else {
      updateBasketAnalyticsObject();
    }

    dispatchCustomEvent("ADD_TO_BASKET_ESI:BASKET_SUBMITTED");
  },
  onSuccess: (_result: SubmissionResult) => {
    const { context, response } = _result;

    dispatchCustomEvent(ADD_TO_BASKET_ESI_SUCCESSFULLY_ADDED, {
      errors: response.errors,
    });

    fireMonetateEventIfReferredByPLP();

    const addedItem = (response.commerceItems || []).find(
      (item) => item.skuId === context.skuId,
    );

    if (!addedItem) {
      return;
    }

    const productInfo: ProductInfo = {
      productId: context.productId,
      price: context.price ?? addedItem?.price,
      title: context.title ?? addedItem?.name,
      skuId: context.skuId,
      variantId: context.variantId,
      qty: context.quantity,
      services: context.servicesAdded.map((service) => ({
        productId: service.skuId,
        skuId: service.skuId,
        itemPrice: service.value,
        qty: context.quantity,
      })),
    };

    dispatchAnalyticsEvent(
      "addtobasketsuccess",
      addToBasketSuccessPayload(
        productInfo,
        context.isExpressCheckout,
        response.basketContent,
      ),
    );

    const basketData = buildBasketUpdateDataFromBasket(response.basketContent);

    trackEvent(AddToBasket, {
      originatingImplementationId: "web-pdp-scaffold-ui",
      triggeringImplementationId: "web-pdp-scaffold-ui",
      type: context.isExpressCheckout ? "shipfromstore" : "standard",
      buttonType: context.isExpressCheckout ? "expresscheckout" : "static",
      products: [
        buildTrackEventProductData(
          context.productId,
          context.quantity,
          context.skuId,
          addedItem,
        ),
      ],
      page: {
        name: window?.jlData?.page?.pageInfo?.pageName,
        type: "pdp scaffold product",
      },
      basket: {
        ...basketData,
        subTotal: response.basket?.subtotal || 0,
      },
    });

    if (!context.isExpressCheckout) {
      dispatchCustomEvent("jl-events.browser.basketQuantityUpdated");
    }

    addPageAction("basketSubmissionSuccess", {
      orderId: response.orderId,
      basketContent: response.basketContent?.length,
      retryCount: response.retryCount + 1,
    });
  },
  onError: (
    _result: SubmissionResult,
    _callbacks: BasketSubmissionCallbacks,
  ) => {
    const { context, response } = _result;

    dispatchCustomEvent("ADD_TO_BASKET_ESI:WARNING_OR_ERROR", {
      errors: response.errors,
    });

    const firstError = response.errors[0];

    _callbacks.setAddToBasketSubmissionError(firstError || {});

    if (!firstError || !firstError.code) {
      return;
    }

    noticeError(`Add to basket POST failed with ${firstError.code}`);

    dispatchAnalyticsEvent(
      "addtobasketerror",
      addToBasketFailurePayload(firstError.message),
      {
        error: {
          type: "pdp:addToBasket",
          message: firstError.message,
          messages: response.errors.map((error) => error.message),
        },
      },
    );

    const shopperId = sha3_256(Cookies.get("shopperId") || "Guest User");
    const errorDetails = {
      shopperId,
      productId: context.productId,
      skuId: context.skuId,
      variantId: context.variantId,
      qty: context.quantity,
      title: context.title,
      errors: JSON.stringify(response.errors),
      errorCode: firstError.code,
      errorMessage: firstError.message,
      responseStatus: response.responseStatus,
      type: "pdp:addToBasket",
      upstream: firstError.originSystem,
      clientId: BASKET_CLIENT_ID,
    };

    addPageAction("add-to-basket-error", errorDetails);

    if (firstError.code === APIErrorCode.OUT_OF_STOCK_ONLINE) {
      addPageAction("a2b-disabled", errorDetails);
      _callbacks.setButtonState({
        addToBasketDisabled: true,
        expressCheckoutDisabled: true,
        buttonText: "Out of stock",
      });
    } else if (
      [
        APIErrorCode.STOCK_EXCEEDED_ERROR,
        APIErrorCode.MAX_PURCHASE_QUANTITY_EXCEEDED_WARNING,
      ].includes(firstError.code)
    ) {
      if (!context.isExpressCheckout) {
        addPageAction("a2b-disabled", errorDetails);
        _callbacks.setButtonState((prevState) => ({
          ...prevState,
          addToBasketDisabled: true,
        }));
      }
    } else if (
      [
        APIErrorCode.MAX_PURCHASE_QUANTITY_EXCEEDED_ERROR,
        APIErrorCode.LINE_ITEMS_EXCEEDS_MAX,
        APIErrorCode.INVALID_PRODUCT_TYPE,
        APIErrorCode.PRODUCT_NOT_AVAILABLE_FOR_ORDERING,
        APIErrorCode.PRODUCT_NOT_FOUND,
        APIErrorCode.QUANTITY_MIN_INVALID,
        APIErrorCode.STOCK_EXCEEDED_ERROR,
      ].includes(firstError.code)
    ) {
      addPageAction("a2b-disabled", errorDetails);
      _callbacks.setButtonState((prevState) => ({
        ...prevState,
        addToBasketDisabled: true,
        expressCheckoutDisabled: true,
      }));
    } else if (firstError.code === APIErrorCode.STOCK_SOURCE_MISMATCH) {
      addPageAction("a2b-disabled", errorDetails);
      _callbacks.setButtonState((prevState) => ({
        ...prevState,
        expressCheckoutDisabled: true,
      }));
    }
  },
  onRetry: (
    _context: SubmissionContext,
    _attemptNumber: number,
    errorMessage: string,
  ) => {
    addPageAction("add-to-basket-retries", {
      retryCount: _attemptNumber,
    });

    noticeError(
      `Error from basket service. Error: ${errorMessage}, Retry Count: ${_attemptNumber}`,
    );
  },
};

export const handleBasketSubmission = async (
  {
    basketApiBaseUrl,
    basketCheckoutHost,
    pimProductId,
    skuId,
    variantId,
    isExpressCheckout,
    isShipFromStore,
    price,
    title,
    services,
    quantity,
  }: BasketSubmissionParams,
  {
    setIsSubmitting,
    setIsExpressSubmitting,
    setAddToBasketSubmissionError,
    setBasket,
    setButtonState,
    focusAddToBasketButton,
  }: BasketSubmissionCallbacks,
) => {
  const submittedQuantity = isExpressCheckout ? 1 : quantity;
  const selectedServices = services.filter((service) => service.selected);

  const context: SubmissionContext = {
    productId: pimProductId,
    skuId,
    variantId,
    isExpressCheckout,
    isShipFromStore,
    price,
    title,
    quantity: submittedQuantity,
    servicesAdded: selectedServices,
  };

  const childItems: AddToBasketChildItemRequest[] = selectedServices.map(
    (service) => ({
      variantId: service.id,
      quantity: submittedQuantity,
    }),
  );

  defaultSubmissionHooks.onStart(context);

  if (isExpressCheckout) {
    setIsExpressSubmitting(true);
  } else {
    setIsSubmitting(true);
  }

  let retryCount = 0;
  let finalResult: AddToBasketResponse = {
    success: false,
    errors: [],
    commerceItems: [],
  };

  for (retryCount; retryCount < MAX_RETRIES; retryCount++) {
    await new Promise<void>((resolve) => {
      setTimeout(() => resolve(), retryCount * 500);
    });

    finalResult = await addSkuToBasket({
      basketApiBaseUrl,
      skuId,
      variantId,
      quantity: submittedQuantity,
      childItems,
      express: isExpressCheckout,
      isShipFromStore,
    });

    const { success, responseStatus } = finalResult;

    const shouldStopRetrying =
      success || (!success && responseStatus === 422) || retryCount === 4;

    if (shouldStopRetrying) {
      break;
    }

    defaultSubmissionHooks.onRetry(
      context,
      retryCount + 1,
      finalResult.errorMessage || "Generic error",
    );
  }

  defaultSubmissionHooks.onSubmitted(context);

  window.setTimeout(() => {
    setIsSubmitting(false);
    setIsExpressSubmitting(false);
  }, RESET_DELAY_MS);

  const basketContent = getBasketItems(finalResult.commerceItems, pimProductId);

  const submissionResult: SubmissionResult = {
    context,
    response: {
      success: finalResult.success ?? false,
      orderId: finalResult.orderId,
      retryCount,
      commerceItems: finalResult.commerceItems,
      basket: finalResult.basket,
      basketContent,
      errors: finalResult.errors,
      responseStatus: finalResult.responseStatus,
    },
  };

  if (finalResult.success) {
    defaultSubmissionHooks.onSuccess(submissionResult);

    setAddToBasketSubmissionError(undefined);

    if (isExpressCheckout) {
      window.location.assign(
        `${basketCheckoutHost}/web/handover?basketId=${finalResult.orderId}&express=true`,
      );
    } else {
      if (basketContent) {
        setBasket(basketContent);
      }

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            focusAddToBasketButton();
          }, 150);
        });
      });
    }

    return;
  }

  defaultSubmissionHooks.onError(submissionResult, {
    setIsSubmitting,
    setIsExpressSubmitting,
    setAddToBasketSubmissionError,
    setBasket,
    setButtonState,
    focusAddToBasketButton,
  });
};
